
import { useState, useEffect } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ArrowLeft, Plus, FolderOpen, Trash2, Edit, MoreVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ColorPicker } from "@/components/color-picker"
import { IconPicker } from "@/components/icon-picker"
import { useAuth } from "@/lib/auth/auth-context"
import { fetchUserCategories, createCategory, updateCategory, deleteCategory } from "@/lib/services/categories"
import type { CustomCategory } from "@/lib/types"
import { toast } from "sonner"

interface CategoriesProps {
  onBack: () => void
}

export function Categories({ onBack }: CategoriesProps) {
  const { user } = useAuth()
  const [categoryType, setCategoryType] = useState<"expense" | "income">("expense")
  const [showAddSheet, setShowAddSheet] = useState(false)
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState<CustomCategory[]>([])
  const [editingCategory, setEditingCategory] = useState<CustomCategory | null>(null)
  const [newCategory, setNewCategory] = useState({
    name: "",
    icon: "💰",
    color: "bg-primary/10 text-primary",
    type: "expense" as "expense" | "income"
  })

  const currentCategories = categories.filter(cat => cat.type === categoryType)

  // Fetch categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      if (!user) return

      try {
        setLoading(true)
        const data = await fetchUserCategories(user.id)
        setCategories(data)
      } catch (error) {
        console.error('Error loading categories:', error)
        toast.error('Failed to load categories')
      } finally {
        setLoading(false)
      }
    }

    loadCategories()
  }, [user])

  const handleAddCategory = () => {
    setEditingCategory(null)
    setNewCategory({
      name: "",
      icon: "💰",
      color: "bg-primary/10 text-primary",
      type: categoryType
    })
    setShowAddSheet(true)
  }

  const handleEditCategoryClick = (category: CustomCategory) => {
    setEditingCategory(category)
    setNewCategory({
      name: category.name,
      icon: category.icon,
      color: category.color,
      type: category.type
    })
    setShowAddSheet(true)
  }

  const handleSaveCategory = async () => {
    if (!user || !newCategory.name.trim()) return

    try {
      setLoading(true)

      if (editingCategory) {
        // Update existing category
        await updateCategory(editingCategory.id, {
          name: newCategory.name.trim(),
          icon: newCategory.icon,
          color: newCategory.color,
        })
        toast.success('Category updated successfully')
      } else {
        // Create new category
        await createCategory(user.id, {
          name: newCategory.name.trim(),
          icon: newCategory.icon,
          color: newCategory.color,
          type: newCategory.type,
          keywords: []
        })
        toast.success('Category created successfully')
      }

      // Reload categories
      const data = await fetchUserCategories(user.id)
      setCategories(data)
      setShowAddSheet(false)
      setEditingCategory(null)
    } catch (error: any) {
      console.error('Error saving category:', error)
      toast.error(error.message || 'Failed to save category')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCategoryClick = async (categoryId: string) => {
    if (!user) return

    try {
      setLoading(true)
      await deleteCategory(categoryId)
      toast.success('Category deleted successfully')

      // Reload categories
      const data = await fetchUserCategories(user.id)
      setCategories(data)
    } catch (error: any) {
      console.error('Error deleting category:', error)
      toast.error(error.message || 'Failed to delete category')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6 p-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-6 w-6" />
        </Button>
        <div>
          <h1 className="text-xl font-bold">Categories</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage expense and income categories</p>
        </div>
      </div>

      {/* Category Type Tabs */}
      <Card className="glassmorphism p-1 border-white/20">
        <Tabs value={categoryType} onValueChange={(v) => setCategoryType(v as "expense" | "income")} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-transparent">
            <TabsTrigger
              value="expense"
              className="data-[state=active]:bg-white/20 data-[state=active]:backdrop-blur-xl"
            >
              Expense Categories
            </TabsTrigger>
            <TabsTrigger
              value="income"
              className="data-[state=active]:bg-white/20 data-[state=active]:backdrop-blur-xl"
            >
              Income Categories
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </Card>

      {/* Add Category Button */}
      <Card className="glassmorphism p-4 border-white/20">
        <Button
          className="w-full gap-2 bg-primary/90 hover:bg-primary backdrop-blur-xl"
          onClick={handleAddCategory}
        >
          <Plus className="h-5 w-5" />
          Add {categoryType === "expense" ? "Expense" : "Income"} Category
        </Button>
      </Card>

      {/* Categories List */}
      <div className="space-y-4">
        {loading ? (
          <Card className="glassmorphism p-8 text-center border-white/20">
            <p className="text-muted-foreground">Loading categories...</p>
          </Card>
        ) : currentCategories.length === 0 ? (
          <Card className="glassmorphism p-8 text-center border-white/20">
            <div className="flex flex-col items-center gap-3">
              <div className="h-16 w-16 rounded-full bg-primary/10 backdrop-blur-xl flex items-center justify-center">
                <FolderOpen className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="font-medium">No {categoryType} categories yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Create your first category to organize your {categoryType === "expense" ? "expenses" : "income"}
                </p>
              </div>
            </div>
          </Card>
        ) : (
          currentCategories.map((category: any) => (
            <Card key={category.id} className="glassmorphism p-4 border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className={`h-12 w-12 rounded-xl ${category.color} backdrop-blur-xl flex items-center justify-center text-2xl`}>
                  {category.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{category.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{category.type}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="backdrop-blur-xl">
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="glassmorphism border-white/20">
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={() => handleEditCategoryClick(category)}
                      disabled={category.isDefault}
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="gap-2 text-destructive"
                      onClick={() => handleDeleteCategoryClick(category.id)}
                      disabled={category.isDefault}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Info Card */}
      <Card className="glassmorphism p-4 border-white/20 bg-primary/5">
        <div className="flex gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 backdrop-blur-xl flex items-center justify-center flex-shrink-0">
            <FolderOpen className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">Organize Your Finances</p>
            <p className="text-xs text-muted-foreground mt-1">
              Create custom categories with icons and colors to better track your spending and income patterns
            </p>
          </div>
        </div>
      </Card>

      {/* Add/Edit Category Dialog */}
      <Dialog open={showAddSheet} onOpenChange={setShowAddSheet}>
        <DialogContent className="w-screen h-screen max-w-full overflow-hidden border-white/20 p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/20 text-left">
            <DialogTitle className="text-2xl font-bold">
              {editingCategory ? 'Edit' : 'Create New'} {categoryType === "expense" ? "Expense" : "Income"} Category
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-6">
              {/* Category Name with Icon */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <span className="text-lg">✏️</span>
                  </div>
                  <Label className="text-base font-semibold">Category Name</Label>
                </div>
                <Input
                  placeholder="e.g., Groceries, Salary, etc."
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="border-white/20 h-14 text-base transition-all focus:shadow-lg focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Icon Picker - Enhanced */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <span className="text-lg">🎨</span>
                  </div>
                  <Label className="text-base font-semibold">Choose Icon</Label>
                </div>
                <Card className="border-white/20 p-5 transition-all hover:shadow-lg hover:border-primary/30">
                  <IconPicker
                    value={newCategory.icon}
                    onChange={(icon) => setNewCategory({ ...newCategory, icon })}
                  />
                </Card>
              </div>

              {/* Color Picker - Enhanced */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <span className="text-lg">🌈</span>
                  </div>
                  <Label className="text-base font-semibold">Choose Color</Label>
                </div>
                <Card className="border-white/20 p-5 transition-all hover:shadow-lg hover:border-primary/30">
                  <ColorPicker
                    value={newCategory.color}
                    onChange={(color) => setNewCategory({ ...newCategory, color })}
                  />
                </Card>
              </div>

              {/* Live Preview - Enhanced */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <span className="text-lg">✨</span>
                  </div>
                  <Label className="text-base font-semibold">Live Preview</Label>
                </div>
                <Card className="p-5 border-white/20 bg-gradient-to-br from-primary/5 via-transparent to-transparent shadow-xl transition-all hover:shadow-2xl">
                  <div className="flex items-center gap-4 p-4 rounded-2xl bg-background/50 border-2 border-white/10 shadow-inner">
                    <div className={`h-20 w-20 rounded-2xl ${newCategory.color} flex items-center justify-center text-4xl transition-all duration-500 shadow-lg hover:scale-105`}>
                      {newCategory.icon}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-xl mb-1">{newCategory.name || "Category Name"}</p>
                      <div className="flex items-center gap-2">
                        <div className={`px-3 py-1 rounded-full ${categoryType === "expense" ? "bg-red-500/10 text-red-600" : "bg-green-500/10 text-green-600"} text-xs font-semibold capitalize flex items-center gap-1`}>
                          {categoryType === "expense" ? "💸" : "💰"} {categoryType}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>

          {/* Action Buttons - Fixed at bottom */}
          <div className="px-6 py-4 border-t border-white/20 bg-gradient-to-br from-transparent via-transparent to-primary/5">
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-white/20 hover:bg-white/10 transition-all h-12 text-base font-medium hover:scale-[1.02] active:scale-[0.98]"
                onClick={() => setShowAddSheet(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all h-12 text-base font-semibold hover:scale-[1.02] active:scale-[0.98]"
                disabled={!newCategory.name || loading}
                onClick={handleSaveCategory}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {editingCategory ? '✓ Update Category' : '+ Add Category'}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
